import React from 'react';
import { useAuth } from 'auth';
import { useLocationService } from 'services/location';
import useAsyncEffect from 'use-async-effect';
import { ApolloClient, InMemoryCache, from } from '@apollo/client';
import { ApolloProvider } from '@apollo/client';
import { createAppSyncHybridLink } from 'utils/graphql/appSyncHybridLink';
import { onError } from '@apollo/client/link/error';
import { message as antMessage } from 'antd';
import i18n from './i18n';

import AppSyncConfig from './app-sync-config.json';
import { buildTypePolicies } from 'graph/cacheTypePolicies';

// @ts-ignore
let subdomain: string = document.location.hostname.replace(/\.gerositus.*/, '');
const config: any =
	import.meta.env.VITE_APP_MODE === 'corporate'
		? AppSyncConfig[import.meta.env.VITE_APP_MODE][subdomain][
		import.meta.env.VITE_APP_ENV
		]
		: AppSyncConfig[import.meta.env.VITE_APP_MODE][
		import.meta.env.VITE_APP_ENV
		];

interface IProps {
	children: any;
	isTokenExpiring?: boolean;
}

export const AuthorizedApolloProvider: React.FC<IProps> = ({
	children,
	isTokenExpiring
}) => {
	const [client, setClient] = React.useState<ApolloClient<any> | null>(null);
	const { isLoading, getToken } = useAuth();
	const location = useLocationService();

	useAsyncEffect(async () => {
		if (isLoading) return;

		// Authentication-error detector.
		// Previously matched only 'Token expired' literal — AppSync's actual
		// error shapes never contain that string, so 401s were silently
		// swallowed: Apollo would apply the optimistic update to the cache,
		// the server would reject the mutation, no toast, no rollback visible
		// to the user, refresh wipes everything. Detect every shape AppSync
		// uses for an authorization failure and force a reload.
		let reloadingForAuth = false;
		// Anti-boucle : `cacheLocation: 'memory'` (PR #57) fait qu'un reload perd
		// le token et déclenche un silent-auth ; s'il échoue (cookie tiers sur
		// localhost, changement de scope…), on retomberait en reload en rafale.
		// On borne à un reload d'auth toutes les 30s via sessionStorage.
		const RELOAD_COOLDOWN_MS = 30_000;
		const triggerAuthReload = (reason: string) => {
			if (reloadingForAuth) return;
			try {
				const last = Number(sessionStorage.getItem('authReloadAt') || 0);
				if (Date.now() - last < RELOAD_COOLDOWN_MS) {
					console.warn('[Apollo errorLink] Auth failure but within cooldown, skipping reload:', reason);
					return;
				}
				sessionStorage.setItem('authReloadAt', String(Date.now()));
			} catch { /* sessionStorage indispo : on laisse passer le reload */ }
			reloadingForAuth = true;
			console.warn('[Apollo errorLink] Auth failure detected → reloading:', reason);
			location.reload();
		};
		const AUTH_MESSAGE_PATTERN =
			/token has expired|token expired|unauthorized|invalid jwt|signature|expired access token|jwt is required/i;
		const errorLink = onError(({ graphQLErrors, networkError }) => {
			if (graphQLErrors) {
				for (const err of graphQLErrors) {
					// AppSync emits `errorType: 'UnauthorizedException'` on the
					// GraphQL response, alongside `message` describing the cause.
					const errorType = (err as { errorType?: string }).errorType;
					if (errorType === 'UnauthorizedException') {
						triggerAuthReload(`graphQL errorType=UnauthorizedException`);
						return;
					}
					// Écriture refusée par le serveur (delete.js / updateWithoutResponse.js) :
					// Apollo annule l'optimistic update, on prévient l'utilisateur au lieu
					// de laisser l'élément « revenir » sans explication.
					if (errorType === 'WriteRejected') {
						antMessage.error(i18n.t('messages.errors.write_rejected'));
						continue;
					}
					if (err.message && AUTH_MESSAGE_PATTERN.test(err.message)) {
						triggerAuthReload(`graphQL message=${err.message}`);
						return;
					}
				}
			}
			if (networkError) {
				const statusCode = (networkError as { statusCode?: number }).statusCode;
				if (statusCode === 401 || statusCode === 403) {
					triggerAuthReload(`network statusCode=${statusCode}`);
					return;
				}
				if (
					networkError.message &&
					AUTH_MESSAGE_PATTERN.test(networkError.message)
				) {
					triggerAuthReload(`network message=${networkError.message}`);
				}
			}
		});

		const getClient = async () => {
			const appSyncLink = await createAppSyncHybridLink({
				appSyncApiUrl: config.ApiUrl,
				// Realtime WebSocket bypasses CloudFront — it talks
				// directly to AppSync via `realtime.<env>.strategy…`.
				// Falls back to the legacy in-line transform when
				// RealtimeUrl is unset (older config blobs).
				appSyncRealtimeUrl: config.RealtimeUrl,
				getJwtToken: getToken
			});

			return new ApolloClient({
				link: from([errorLink, appSyncLink]),
				cache: new InMemoryCache({ resultCaching: false, typePolicies: buildTypePolicies() })
			});
		};

		const finalClient = await getClient();
		setClient(finalClient);
	}, [isLoading, getToken]);

	// Afficher un indicateur si le token est sur le point d'expirer
	if (isTokenExpiring) {
		console.warn('Token expiration warning is active');
	}

	return client ? (
		<ApolloProvider client={client}>{children}</ApolloProvider>
	) : null;
};
